/**
 * Life4Earth V2 — Centralized Date Formatting
 *
 * Respects the user's calendar preference (localStorage `l4e_calendar`).
 * When set to "hijri", dates render in the Islamic (Umm al-Qura) calendar.
 * Otherwise, dates render in the Gregorian calendar.
 *
 * All functions accept ISO date strings (YYYY-MM-DD) or Date objects.
 *
 * Uses `Intl.DateTimeFormat` under the hood, so the browser handles the
 * actual calendar math — no external library required.
 */

/* ── Calendar preference ─────────────────────────────────────────── */

/** Read the current calendar preference. */
export function getCalendarPref(): 'gregorian' | 'hijri' {
  try {
    return (localStorage.getItem('l4e_calendar') as any) === 'hijri' ? 'hijri' : 'gregorian';
  } catch {
    return 'gregorian';
  }
}

/** Get the current i18n language code (defaults to 'en'). */
function getLang(): string {
  try {
    return localStorage.getItem('l4e_language') || document.documentElement.lang || 'en';
  } catch {
    return 'en';
  }
}

/** Build an Intl locale string with the correct calendar extension. */
function buildLocale(): string {
  const lang = getLang();
  const cal = getCalendarPref();
  // BCP 47 Unicode calendar extension: -u-ca-islamic-umalqura
  return cal === 'hijri' ? `${lang}-u-ca-islamic-umalqura` : lang;
}

/* ── Public formatting functions ─────────────────────────────────── */

/**
 * Format a date as a medium-length string.
 * Gregorian example: "25 Jan 2024"
 * Hijri example:     "١٣ رجب ١٤٤٥"
 */
export function formatDateMedium(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input + (input.length === 10 ? 'T00:00:00' : '')) : input;
  if (isNaN(d.getTime())) return String(input);
  return new Intl.DateTimeFormat(buildLocale(), {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as a short string (DD/MM/YYYY or equivalent).
 * Gregorian example: "25/01/2024"
 * Hijri example:     "١٣/٠٧/١٤٤٥"
 */
export function formatDateShort(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input + (input.length === 10 ? 'T00:00:00' : '')) : input;
  if (isNaN(d.getTime())) return String(input);
  return new Intl.DateTimeFormat(buildLocale(), {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date as a long string with weekday.
 * Gregorian example: "Thursday, January 25, 2024"
 * Hijri example:     "الخميس، ١٣ رجب ١٤٤٥ هـ"
 */
export function formatDateLong(input: string | Date): string {
  const d = typeof input === 'string' ? new Date(input + (input.length === 10 ? 'T00:00:00' : '')) : input;
  if (isNaN(d.getTime())) return String(input);
  return new Intl.DateTimeFormat(buildLocale(), {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(d);
}

/**
 * Format a date using the DD-MMM-YYYY pattern (backward-compatible).
 * This replaces the old `isoToDdMmmYyyy` and `fmtAsOfDate` functions.
 * Gregorian: "25-Jan-2024"
 * Hijri: uses medium format since hyphenated Hijri isn't standard.
 */
export function formatDateDdMmmYyyy(input: string | Date): string {
  if (getCalendarPref() === 'hijri') {
    return formatDateMedium(input);
  }
  // Gregorian: keep the existing DD-MMM-YYYY format for backward compat
  const d = typeof input === 'string' ? new Date(input + (input.length === 10 ? 'T00:00:00' : '')) : input;
  if (isNaN(d.getTime())) return String(input);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const dd = String(d.getDate()).padStart(2, '0');
  const mmm = months[d.getMonth()];
  const yyyy = d.getFullYear();
  return `${dd}-${mmm}-${yyyy}`;
}

/**
 * Format a date as MM/DD/YYYY (US) or locale-appropriate.
 * This replaces the old `isoToUS` function.
 * When Hijri, uses the short format instead.
 */
export function formatDateUS(input: string | Date): string {
  if (getCalendarPref() === 'hijri') {
    return formatDateShort(input);
  }
  const d = typeof input === 'string' ? new Date(input + (input.length === 10 ? 'T00:00:00' : '')) : input;
  if (isNaN(d.getTime())) return String(input);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  const yyyy = d.getFullYear();
  return `${mm}/${dd}/${yyyy}`;
}
